/**
 * UKPILViewerEmbed — Thin wrapper around `RegulatoryViewerEmbed`
 * bound to the UK MHRA PIL family's API client.  See
 * SBAViewerEmbed.tsx for the full rationale; same shape applies.
 */
import { forwardRef, useImperativeHandle, useRef } from 'react';
import type { ChunkMetadata, TOCEntry } from '../../types/viewer';
import { ukPilApi } from '../../api/createViewerApi';
import RegulatoryViewerEmbed, {
  type ChunkAnchor,
  type RegulatoryViewerHandle,
} from './RegulatoryViewerEmbed';

export type UKPILViewerHandle = RegulatoryViewerHandle;

interface UKPILViewerEmbedProps {
  docId: number;
  highlightChunkIds?: number[];
  chunkAnchors?: Record<number, ChunkAnchor[]>;
  /** page_num -> anchor for a chunk-free (progressive-read) citation.
   *  Forwarded verbatim to RegulatoryViewerEmbed via the props spread. */
  pageAnchors?: Record<number, ChunkAnchor[]> | undefined;
  highlightTerm?: string | null;
  highlightTerms?: string[] | null;
  highlightColors?: (string | undefined)[] | null;
  landingPage?: number | undefined;
  onChunkClick?: (chunk: ChunkMetadata) => void;
  visibleSectionNumbers?: string[] | undefined;
  onTocLoaded?: ((entries: TOCEntry[]) => void) | undefined;
  /** Hide the TOC sidebar (NICE HTA demo docs). Forwarded to RegulatoryViewerEmbed. */
  showToc?: boolean;
}

const UKPILViewerEmbed = forwardRef<UKPILViewerHandle, UKPILViewerEmbedProps>(
  function UKPILViewerEmbed(props, ref) {
    const innerRef = useRef<RegulatoryViewerHandle>(null);
    useImperativeHandle(ref, () => ({
      scrollToPage: (n) => innerRef.current?.scrollToPage(n),
      highlightChunks: (ids) => innerRef.current?.highlightChunks(ids),
      scrollToSection: (s, opts) => innerRef.current?.scrollToSection(s, opts) ?? false,
    }));
    return (
      <RegulatoryViewerEmbed
        ref={innerRef}
        viewerApi={ukPilApi}
        {...props}
      />
    );
  },
);

export default UKPILViewerEmbed;
